import { DateRangePicker } from '@src/components/DateRangeCalendar/DateRangePicker';
import { RandomGiftFunnels } from './RandomGiftFunnels';
import { GiftFunnels } from './GiftFunnels';
import { PaymentJourney } from './PaymentJourney';
import { showDialog } from '@src/components/Dialog/Dialog';
import { PREMIUM_TIERS } from '@src/components/PremiumBadge/tierIdentity';
import {
  calendarDate,
  paymentDateRange,
  type PaymentPeriod,
  type PaymentTimeZone,
} from './paymentDates';
import { PremiumFunnels } from './PremiumFunnels';
import { PaymentReasons } from './PaymentReasons';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CopyIcon } from '@src/components/icons/CopyIcon';
import { showToast } from '@src/components/Toast';
import { isDotCom } from '@src/site';
import { Button, Textarea, Spinner, Hint } from '@src/components';
import { ParticipantTable } from '@src/components/ParticipantTable/ParticipantTable';
import './AdminPayments.css';
import { PaymentAnalytics, type PriceRevenue, type ReasonCount } from './PaymentAnalytics';

export type Payment = {
  base_offer_code?: string | null;
  paid_at?: string | null;
  organizer_history?: { years: string[]; returning: boolean };
  gift_events?: Payment['offer_views'];
  offer_views?: { base_offer_code?: string | null; event_name?: string; variant?: string | null; experiment?: string | null; id: number; created_at: string; page: string; placement: string | null; feature: string | null; offer_code: string | null; amount_minor: string | null; currency: string | null }[];
  id: number;
  user_id: number;
  offer_id: number;
  offer_name: string | null;
  offer_code: string | null;
  offer_group?: string | null;
  offer_experiment?: string | null;
  email: string | null;
  user_is_premium: boolean | null;
  reference_code: string;
  status: string;
  bank_status?: string | null;
  email_text: string | null;
  comment: string | null;
  survey_reasons?: string | null;
  survey_comment?: string | null;
  amount: string;
  created_at: string;
  updated_at: string;
};
function displayStatus(payment: Payment) {
  return payment.bank_status === 'REFUNDED' ? 'refunded' : payment.status;
}

type Result = {
  payments: Payment[];
  total: number;
  allTimeTotal?: number;
  page: number;
  pageSize: number;
  currency: string;
  paidTotal: string;
  revenueByAudience?: { organizers: string; gifts: string; random: string };
  priceBreakdown?: PriceRevenue[];
  reasonBreakdown?: ReasonCount[];
};

export function AdminPayments({ onDenied }: { onDenied: () => void }) {
  return <PaymentGroup filter="all" onDenied={onDenied} />;
}

function PaymentGroup({
  filter,
  onDenied,
}: {
  filter: 'all';
  onDenied: () => void;
}) {
  const { t, i18n } = useTranslation();
  const site = isDotCom() ? 'com' : 'ru';
  const [funnelTab, setFunnelTab] = useState<'organizers' | 'gifts' | 'random'>('organizers');
  const [paymentStatus, setPaymentStatus] = useState('paid');
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [period, setPeriod] = useState<PaymentPeriod>('today');
  const [timeZone, setTimeZone] = useState<PaymentTimeZone>('Europe/Moscow');
  const [customFrom, setCustomFrom] = useState(() => calendarDate(new Date(), timeZone));
  const [customTo, setCustomTo] = useState(() => calendarDate(new Date(), timeZone));
  const [pageSize, setPageSize] = useState(50);
  const range = paymentDateRange(period, timeZone, customFrom, customTo);
  const from = range?.from;
  const to = range?.to;
  const rangeValid = range !== null;
  const ru = i18n.language.startsWith('ru');
  const offerLabel = (code: string | null) => ['giftee_gift','giftee_gift_g2'].includes(code ?? '') ? (ru ? 'Подарок подопечному' : 'Giftee gift') : code ? PREMIUM_TIERS[code as keyof typeof PREMIUM_TIERS]?.[ru ? 'ru' : 'en'] ?? (['full_a', 'full_b', 'legacy_full'].includes(code) ? (ru ? 'Полный доступ' : 'Full access') : code) : null;
  const featureLabel = (feature: string | null) => !feature || feature === 'all' ? null : ({
    themes: ru ? 'Темы' : 'Themes', avatars: ru ? 'Аватары' : 'Avatars',
    account: ru ? 'Аккаунт' : 'Account', organizers: ru ? 'Организаторы' : 'Organizers',
    history: ru ? 'История' : 'History', questions: ru ? 'Вопросы' : 'Questions',
    draw: ru ? 'Жеребьёвка' : 'Draw', domain: ru ? 'Личный адрес' : 'Custom address',
    no_ads: ru ? 'Без рекламы' : 'No ads',
  } as Record<string, string>)[feature] ?? feature;
  useEffect(() => {
    if (search.trim() === query) return;
    const timer = window.setTimeout(() => {
      setQuery(search.trim());
      setPage(1);
    }, 300);
    return () => window.clearTimeout(timer);
  }, [search, query]);
  const [loadedResult, setResult] = useState<(Result & { audience: string }) | null>(null);
  const result = loadedResult?.audience === funnelTab ? loadedResult : null;
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(true);
  const [refreshVersion, setRefreshVersion] = useState(0);
  useEffect(() => {
    const timer = window.setInterval(() => setRefreshVersion(version => version + 1), 180_000);
    return () => window.clearInterval(timer);
  }, []);
  const [updated, setUpdated] = useState<Date | null>(null);
  useEffect(() => {
    if (!rangeValid) return;
    const controller = new AbortController();
    let busy = false;
    async function load() {
      if (busy) return;
      busy = true;
      setLoading(true);
      try {
        const parameters = new URLSearchParams({
          filter,
          page: String(page),
          pageSize: String(pageSize),
          site,
        });
        parameters.set('status', paymentStatus);
        parameters.set('audience', funnelTab);
        if (filter === 'all') {
          if (query) parameters.set('search', query);
          if (from) parameters.set('from', from);
          if (to) parameters.set('to', to);
        }
        const response = await fetch(`/api/admin/payments?${parameters}`, {
          credentials: 'same-origin',
          cache: 'no-store',
          signal: controller.signal,
        });
        if (response.status === 401 || response.status === 403) {
          onDenied();
          return;
        }
        if (!response.ok) throw new Error('Payment list unavailable');
        const data: Result = await response.json();
        if (!Array.isArray(data.payments)) throw new Error('Invalid payment list');
        if (!controller.signal.aborted) {
          setResult({ ...data, audience: funnelTab });
          if (data.page !== page) setPage(data.page);
          setError(false);
          setUpdated(new Date());
        }
      } catch {
        if (!controller.signal.aborted) setError(true);
      } finally {
        busy = false;
        if (!controller.signal.aborted) setLoading(false);
      }
    }
    void load();
    return () => {
      controller.abort();
    };
    // onDenied closes the parent page; it is deliberately not a fetch dependency.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, page, site, query, from, to, rangeValid, pageSize, paymentStatus, funnelTab, refreshVersion]);
  const date = (value: string) => {
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime())
      ? '—'
      : parsed.toLocaleString(i18n.language, { timeZone });
  };
  const content = (
    <section aria-label={t(`admin.payments.${filter}`)} className="admin-payments">
      <h2>{t('admin.tabs.premium')}</h2>
      <div className="admin-payments__toolbar">
        <p>
          {t('admin.payments.newestFirst')} · {t('admin.payments.refreshNote')}
          {updated &&
            ` · ${t('admin.payments.updated')}: ${updated.toLocaleTimeString(i18n.language, { timeZone })}`}
        </p>
      </div>
      {filter === 'all' && (
        <div className="admin-payments__filters">
          <label className="admin-payments__search">
            {t('admin.payments.search')}
            <input
              type="search"
              maxLength={200}
              value={search}
              placeholder={t('admin.payments.searchHint')}
              onChange={(event) => setSearch(event.target.value)}
            />
          </label>
          <label>
            {t('admin.payments.period')}
            <select
              aria-label={t('admin.payments.period')}
              value={period}
              onChange={(event) => {
                setPeriod(event.target.value as PaymentPeriod);
                setPage(1);
              }}
            >
              {['today', 'yesterday', 'threeDays', 'week', 'month', 'all', 'custom'].map(
                (value) => (
                  <option key={value} value={value}>
                    {t(`admin.payments.periods.${value}`)}
                  </option>
                ),
              )}
            </select>
          </label>
          <label>
            {t('admin.payments.paymentStatus')}
            <select
              aria-label={t('admin.payments.paymentStatus')}
              value={paymentStatus}
              onChange={(event) => {
                setPaymentStatus(event.target.value);
                setPage(1);
              }}
            >
              <option value="paid">{t('admin.payments.onlyPaid')}</option>
              <option value="all">{t('admin.payments.allStatuses')}</option>
            </select>
          </label>
          <label>
            {ru ? 'Часовой пояс' : 'Time zone'}
            <select
              aria-label={ru ? 'Часовой пояс' : 'Time zone'}
              value={timeZone}
              onChange={(e) => {
                setTimeZone(e.target.value as PaymentTimeZone);
                setPage(1);
              }}
            >
              <option value="America/Los_Angeles">{ru ? 'Лос-Анджелес' : 'Los Angeles'}</option>
              <option value="Europe/Moscow">{ru ? 'Москва' : 'Moscow'}</option>
            </select>
          </label>
          {period === 'custom' && <DateRangePicker calendarOnly value={{ start: customFrom, end: customTo }} onChange={value => {
            setCustomFrom(value.start); setCustomTo(value.end); setPage(1);
          }} />}

        </div>
      )}
      <div className="payment-funnel-tabs" role="tablist" aria-label={ru ? 'Воронки платежей' : 'Payment funnels'}>
        {(['organizers','gifts','random'] as const).map(tab => <Button key={tab} role="tab" id={`funnel-tab-${tab}`} aria-selected={funnelTab === tab} aria-controls="payment-funnel-panel" tabIndex={funnelTab === tab ? 0 : -1} onKeyDown={event => { if (['ArrowLeft','ArrowRight','Home','End'].includes(event.key)) { event.preventDefault(); const tabs = ['organizers','gifts','random'] as const; const next = event.key === 'Home' ? tabs[0] : event.key === 'End' ? tabs[2] : tabs[(tabs.indexOf(tab) + (event.key === 'ArrowRight' ? 1 : 2)) % tabs.length]; setFunnelTab(next); setPage(1); document.getElementById(`funnel-tab-${next}`)?.focus(); } }} variant={funnelTab === tab ? 'primary' : 'secondary'} onClick={() => { setFunnelTab(tab); setPage(1); }}>{tab === 'organizers' ? (ru ? 'Организаторы' : 'Organizers') : tab === 'gifts' ? (ru ? 'Подарки участников' : 'Participant gifts') : (ru ? 'Случайные подарки' : 'Random gifts')}</Button>)}
      </div>
      {!rangeValid && (
        <Hint tone="error">
          {ru ? 'Выберите корректный диапазон дат.' : 'Select a valid date range.'}
        </Hint>
      )}
      {filter === 'all' && rangeValid && result && (
        <PaymentAnalytics
          key={funnelTab}
          title={funnelTab === 'random' ? (ru ? 'Выручка от анонимных подарков' : 'Anonymous gift revenue') : funnelTab === 'gifts' ? (ru ? 'Выручка от подарков участников' : 'Participant gift revenue') : (ru ? 'Выручка от организаторов' : 'Organizer revenue')}
          revenueByAudience={result.revenueByAudience}
          total={result.paidTotal}
          currency={result.currency}
          prices={result.priceBreakdown ?? []}
          reasons={result.reasonBreakdown ?? []}
          showReasons={funnelTab === 'organizers'}
        />
      )}
      <div role="tabpanel" id="payment-funnel-panel" aria-labelledby={`funnel-tab-${funnelTab}`}>
      {filter === 'all' && funnelTab === 'organizers' && (
        <PremiumFunnels
          onOpenPayment={id => showDialog({ title: ru ? 'Платёж и путь пользователя' : 'Payment and user journey', mobileFullScreen: true,
            children: <PaymentJourneyLoader id={id} site={site} ru={ru} date={date} offerLabel={offerLabel} featureLabel={featureLabel} onDenied={onDenied} /> })}
          refreshVersion={refreshVersion}
          currency={result?.currency ?? (site === 'com' ? 'USD' : 'RUB')}
          from={from}
          to={to}
          valid={rangeValid}
          timeZone={timeZone}
          onDenied={onDenied}
        />
      )}
      {funnelTab === 'random' && <RandomGiftFunnels refreshVersion={refreshVersion} currency={result?.currency ?? (site === 'com' ? 'USD' : 'RUB')} from={from} to={to} valid={rangeValid} timeZone={timeZone} onDenied={onDenied} />}
      {funnelTab === 'gifts' && <GiftFunnels refreshVersion={refreshVersion} currency={result?.currency ?? (site === 'com' ? 'USD' : 'RUB')} from={from} to={to} valid={rangeValid} timeZone={timeZone} onDenied={onDenied} />}
      </div>
      {error && <Hint tone="error">{t('admin.payments.error')}</Hint>}
      {loading && !result && <Spinner label={t('account.loading')} />}
      {result && rangeValid && (
        <div className="admin-payments__filters" aria-live="polite">
          <p data-testid="payment-count">
            {ru ? 'Показано' : 'Showing'}{' '}
            {result.total ? (result.page - 1) * result.pageSize + 1 : 0}–
            {Math.min(result.page * result.pageSize, result.total)} {ru ? 'из' : 'of'}{' '}
            {result.total} {ru ? 'по фильтрам' : 'matching payments'} ·{' '}
            {ru ? 'Всего за всё время' : 'All-time total'}: {result.allTimeTotal ?? '—'}
          </p>
          <label>
            {ru ? 'На странице' : 'Per page'}
            <select
              aria-label={ru ? 'На странице' : 'Per page'}
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setPage(1);
              }}
            >
              {[10, 25, 50, 100].map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          </label>
        </div>
      )}
      {result && rangeValid && (
        <ParticipantTable
          embedded
          testId={`admin-payments-${filter}-table`}
          pagination={{
            page,
            pageCount: Math.max(1, Math.ceil(result.total / result.pageSize)),
            total: result.total,
            pageSize: result.pageSize,
            onChange: (next) => {
              setResult(null);
              setPage(next);
            },
            previousLabel: t('admin.payments.previous'),
            nextLabel: t('admin.payments.next'),
            pageLabel: (value) => t('admin.payments.page', { page: value }),
          }}
        >
          <div
            className="admin-payments__scroll"
            role="region"
            aria-label={t('admin.payments.title')}
            tabIndex={0}
          >
            <table aria-label={t(`admin.payments.${filter}`)}>
              <thead>
                <tr>
                  <th scope="col" aria-label={ru ? 'Номер строки' : 'Row number'}>№</th>
                  {['id', 'user', 'status', 'amount', 'organizerHistory', 'created', ...(funnelTab === 'organizers' ? ['reasons'] : [])].map(
                    (key) => (
                      <th
                        key={key}
                        scope="col"
                        className={
                          key === 'status'
                            ? 'admin-payments__status-cell'
                            : key === 'user'
                              ? 'admin-payments__user-cell'
                              : undefined
                        }
                      >
                        {key === 'amount' ? (ru ? 'Сумма и предложение' : 'Amount and offer') : key === 'offerViews' ? (ru ? 'Предложения: просмотры и клики' : 'Offers: views and clicks') : key === 'organizerHistory' ? (ru ? 'Опыт организатора' : 'Organizer history') : t(`admin.payments.${key}`)}
                      </th>
                    ),
                  )}
                </tr>
              </thead>
              <tbody>
                {result.payments.map((payment, index) => (
                  <tr
                    key={payment.id}
                    className={
                      calendarDate(new Date(payment.created_at), timeZone) ===
                      calendarDate(new Date(), timeZone)
                        ? 'admin-payments__today'
                        : undefined
                    }
                  >
                    <td>{(result.page - 1) * result.pageSize + index + 1}</td>
                    <td>#{payment.id}</td>
                    <td className="admin-payments__user-cell">
                      <div className="admin-payments__user">
                        <button type="button" className="admin-payments__journey-link" onClick={() => showDialog({
                          title: ru ? 'Путь пользователя' : 'User journey', mobileFullScreen: true,
                          children: <PaymentJourney payment={payment} ru={ru} currency={result.currency} date={date} offerLabel={offerLabel} featureLabel={featureLabel} />,
                        })}><strong>{payment.user_id}</strong><span>{payment.email || '—'}</span></button>
                        <div className="admin-payments__user-comment">
                          <PaymentComment
                            payment={payment}
                            onDenied={onDenied}
                            onSaved={(saved) =>
                              setResult(
                                (current) =>
                                  current && {
                                    ...current,
                                    payments: current.payments.map((row) =>
                                      row.id === payment.id ? { ...row, ...saved } : row,
                                    ),
                                  },
                              )
                            }
                          />
                        </div>
                      </div>
                    </td>
                    <td className="admin-payments__status-cell">
                      <div className="admin-payments__statuses">
                        <span
                          className={`admin-payments__status ${
                            ['requested', 'pending', 'paid'].includes(displayStatus(payment))
                              ? `admin-payments__status--${displayStatus(payment)}`
                              : ''
                          }`}
                        >
                          {t(`admin.payments.statuses.${displayStatus(payment)}`, {
                            defaultValue: displayStatus(payment),
                          })}
                        </span>
                        <span
                          className={`admin-payments__status ${
                            payment.user_is_premium === true ? 'admin-payments__status--paid' : ''
                          }`}
                        >
                          {t(
                            payment.user_is_premium === true
                              ? 'admin.payments.premiumActive'
                              : payment.user_is_premium === false
                                ? 'admin.payments.premiumInactive'
                                : 'admin.payments.premiumUnknown',
                          )}
                        </span>
                      </div>
                    </td>
                    <td className="admin-payments__amount">
                      <strong>{new Intl.NumberFormat(i18n.language, {
                        style: 'currency',
                        currency: result.currency,
                      }).format(Number(payment.amount))}</strong>
                      <div className="admin-payments__offer-name">{payment.offer_name || '—'}</div>
                      {payment.offer_group && <small title={payment.offer_experiment ?? undefined}>{ru ? 'Группа' : 'Group'} {payment.offer_group}</small>}
                      <small>
                        #{payment.offer_id} · {payment.offer_code || '—'}
                      </small>
                    </td>
                    <td className="admin-payments__organizer-history">
                      {payment.organizer_history ? <>
                        <strong>{payment.organizer_history.returning ? (ru ? 'Возвращающийся организатор' : 'Returning organizer') : payment.organizer_history.years.length ? (ru ? 'Новый организатор' : 'New organizer') : (ru ? 'Пока не организовывал' : 'No organizer history')}</strong>
                        <div>{payment.organizer_history.years.join(', ')}</div>
                      </> : '—'}
                    </td>
                    <td className="admin-payments__dates">
                      <time dateTime={payment.created_at}>{date(payment.created_at)}</time>
                      {calendarDate(new Date(payment.created_at), timeZone) ===
                        calendarDate(new Date(), timeZone) && (
                        <small>{t('admin.payments.today')}</small>
                      )}
                    </td>
                    {funnelTab === 'organizers' && <td className="admin-payments__reasons-cell">
                      <PaymentReasons
                        reasons={payment.survey_reasons}
                        comment={payment.survey_comment}
                      />
                    </td>}
                  </tr>
                ))}
                {!result.payments.length && (
                  <tr>
                    <td colSpan={funnelTab === 'organizers' ? 8 : 7}>{t('admin.payments.empty')}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </ParticipantTable>
      )}
    </section>
  );
  return content;
}

export function CopyValue({
  value,
  label,
  displayText,
}: {
  value: string;
  label: string;
  displayText?: string;
}) {
  const { t } = useTranslation();
  async function copy() {
    try {
      await navigator.clipboard.writeText(value);
      showToast(t('admin.payments.copied'), 'success');
    } catch {
      showToast(t('box.organizer.copyFailed'));
    }
  }
  return (
    <button
      type="button"
      className="admin-payments__copy"
      aria-label={label}
      title={label}
      onClick={() => void copy()}
    >
      <span>{displayText ?? value}</span>
      <CopyIcon />
    </button>
  );
}

function PaymentComment({
  payment,
  onDenied,
  onSaved,
}: {
  payment: Payment;
  onDenied: () => void;
  onSaved: (saved: { comment: string | null; updated_at: string }) => void;
}) {
  const { t } = useTranslation();
  const [edit, setEdit] = useState<{ draft: string; original: string | null } | null>(null);
  const savingRef = useRef(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  async function save() {
    if (!edit || savingRef.current) return;
    if ((edit.draft.trim() ? edit.draft : null) === edit.original) {
      setEdit(null);
      return;
    }
    savingRef.current = true;
    setSaving(true);
    setError(null);
    try {
      const response = await fetch(`/api/admin/payments/${payment.id}/comment`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ comment: edit.draft, expectedComment: edit.original }),
      });
      if ([401, 403].includes(response.status)) {
        onDenied();
        return;
      }
      if (response.status === 409) {
        setError(t('admin.payments.commentConflict'));
        return;
      }
      if (!response.ok) throw new Error('Comment unavailable');
      const saved = await response.json();
      if (
        !saved ||
        Array.isArray(saved) ||
        !(saved.comment === null || typeof saved.comment === 'string') ||
        typeof saved.updated_at !== 'string'
      )
        throw new Error('Invalid saved comment');
      onSaved(saved);
      setEdit(null);
      showToast(t('admin.payments.commentSaved'), 'success');
    } catch {
      setError(t('admin.payments.commentError'));
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  }
  if (!edit)
    return (
      <button
        type="button"
        className="admin-payments__comment-edit"
        aria-label={t('admin.payments.editCommentFor', { id: payment.id })}
        title={t('admin.payments.editComment')}
        onClick={() => {
          setError(null);
          setEdit({ draft: payment.comment || '', original: payment.comment ?? null });
        }}
      >
        {payment.comment || t('admin.payments.addComment')}
      </button>
    );
  return (
    <div
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) void save();
      }}
    >
      <Textarea
        autoFocus
        label={t('admin.payments.comment')}
        value={edit.draft}
        maxLength={10000}
        rows={3}
        disabled={saving}
        onValueChange={(draft) => setEdit({ ...edit, draft })}
      />
      {error && <p role="alert">{error}</p>}
      <div className="admin-payments__comment-actions">
        <Button
          size="small"
          variant="secondary"
          loading={saving}
          disabled={saving}
          onClick={() => void save()}
        >
          {t('admin.payments.saveComment')}
        </Button>
      </div>
    </div>
  );
}

function PaymentJourneyLoader({ id, site, ru, date, offerLabel, featureLabel, onDenied }: {
  id: number; site: string; ru: boolean; date: (value: string) => string;
  offerLabel: (code: string | null) => string | null; featureLabel: (code: string | null) => string | null;
  onDenied: () => void;
}) {
  const [data, setData] = useState<Result | null>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    const controller = new AbortController();
    const params = new URLSearchParams({ filter: 'all', paymentId: String(id), site });
    void fetch(`/api/admin/payments?${params}`, { credentials: 'same-origin', cache: 'no-store', signal: controller.signal })
      .then(async response => {
        if ([401, 403].includes(response.status)) { onDenied(); throw new Error('Not authorized'); }
        if (!response.ok) throw new Error('Payment unavailable');
        const result: Result = await response.json();
        if (!result.payments?.some(payment => payment.id === id)) throw new Error('Payment not found');
        if (!controller.signal.aborted) setData(result);
      }).catch(() => { if (!controller.signal.aborted) setFailed(true); });
    return () => controller.abort();
  }, [id, site, onDenied]);
  if (failed) return <Hint tone="error">{ru ? 'Не удалось загрузить платёж. Закройте окно и попробуйте ещё раз.' : 'Could not load payment. Close this window and try again.'}</Hint>;
  const payment = data?.payments.find(item => item.id === id);
  if (!payment || !data) return <Spinner label={ru ? 'Загрузка платежа' : 'Loading payment'} />;
  return <PaymentJourney payment={payment} ru={ru} currency={data.currency} date={date} offerLabel={offerLabel} featureLabel={featureLabel} />;
}
